import { readsApi } from './api';

// ── Owner search: name → UUID ──────────────────────────────────────────────
// Operators do not know UUIDs. Every finance surface that used to demand a
// pasted id resolves it through this endpoint instead: type a name, pick the
// record, and the id never has to be seen.
//
// FastAPI reads (:8000), gated on the same chekit-level permission as the rest
// of the admin read surface.

export type OwnerSearchType = 'user' | 'business' | 'branch';

interface OwnerSearchCommon {
  ownerId: string;
  /** Display name — what the operator actually searched for. */
  label: string;
  status: string | null;
  createdAt: string | null;
}

/** Role flags are what tell two identically-named people apart. */
export interface UserOwnerResult extends OwnerSearchCommon {
  ownerType: 'user';
  phone: string | null;
  email: string | null;
  isCustomer: boolean;
  isRider: boolean;
  isBusinessUser: boolean;
  isChekitStaff: boolean;
}

export interface BusinessOwnerResult extends OwnerSearchCommon {
  ownerType: 'business';
  phone: string | null;
  email: string | null;
  /** The registering owner — two businesses can share a trading name. */
  ownerName: string | null;
}

export interface BranchOwnerResult extends OwnerSearchCommon {
  ownerType: 'branch';
  phone: string | null;
  /** A branch name alone ("Westlands") is meaningless without its business. */
  businessName: string | null;
  city: string | null;
}

export type OwnerSearchResult =
  | UserOwnerResult
  | BusinessOwnerResult
  | BranchOwnerResult;

export interface OwnerSearchParams {
  type: OwnerSearchType;
  q: string;
  limit?: number;
}

export const searchService = {
  searchOwners: ({ type, q, limit = 20 }: OwnerSearchParams) =>
    readsApi.get<OwnerSearchResult[]>('/admin/search/owners', { type, q, limit }),
};
